import React, { Component } from 'react';
import styled from 'styled-components';
import {
  EQ,
  NOT,
  LT,
  LTE,
  GT,
  GTE,
  BEFORE,
  AFTER,
  MORE_THAN,
  LESS_THAN,
  ONE_OF,
  NOT_ONE_OF,
  getOperatorSymbol,
} from '../../util/Operators';
import ColumnTypeData, {
  BOOL,
  DATETIME,
  DATE,
  STRING,
} from '../../util/ColumnType';
import DateTimePicker from '../DateTimePicker';
import DatetimeButtonGroup from '../DatetimeButtonGroup';
import ColumnRenderer from '../../util/ColumnRenderer';
import ColumnsSelector from '../ColumnsSelector';
import { WHERE } from '../../util/enums/EnumLayers';
import {
  DAY,
  WEEK,
  MONTH,
  YEAR,
  dateOptions,
  QUARTER,
} from '../../util/enums/EnumDateTimeAggregation';
import InputWithFocus from '../InputWithFocus';
import {
  FILTER_OPERATOR_BUTTON,
  FILTER_VALUE_INPUT,
} from '../../constants/test-ids';

const TableRowTopAlign = styled.tr`
  > td {
    vertical-align: top;
  }
`;

const DATE_TIME = 'date time';

const RANGE = {
  hour: [0, 1, 2, 3, 4, 6, 8, 10, 12, 18, 24, 36],
  day: [0, 1, 2, 3, 4, 5, 6, 7, 10, 12, 14, 15, 30],
  week: [0, 1, 2, 3, 4, 5, 6, 8, 10, 12, 26, 52],
  month: [0, 1, 2, 3, 4, 5, 6, 9, 12, 18, 24],
};

export const TODAY = 'today';
export const YESTERDAY = 'yesterday';
export const THIS_WEEK = 'this week';
export const LAST_WEEK = 'last week';
export const THIS_MONTH = 'this month';
export const LAST_MONTH = 'last month';
export const THIS_YEAR = 'this year';
export const LAST_YEAR = 'last year';
export const THIS_QUARTER = 'this quarter';
export const LAST_QUARTER = 'last quarter';
export const ALL_SPECIFIC_TIMES = [
  TODAY,
  YESTERDAY,
  THIS_WEEK,
  LAST_WEEK,
  THIS_MONTH,
  LAST_MONTH,
  THIS_YEAR,
  LAST_YEAR,
  THIS_QUARTER,
  LAST_QUARTER,
];

class ModalWhere extends Component {
  static modalType() {
    return WHERE;
  }

  static reducer(modals) {
    return modals.whereModal;
  }

  static title() {
    return 'Filter';
  }

  static subTitle() {
    return (
      <span>
        Create a filter to find results that fulfill a specific condition
      </span>
    );
  }

  componentDidMount() {
    const { forcedColumn, userInteraction } = this.props;
    if (forcedColumn) {
      // This happens if this modal is being used by dashboard filters
      // If so, select the forced column (as only this column can be used)
      userInteraction({
        section: 'lhs',
        columnName: forcedColumn.name(),
        extra: forcedColumn.type(),
        clauseIndex: 0,
      })();
    }
  }

  buildDateTimeAggregationChooser(state, userInteraction, columns) {
    const aggCol = state.lhs;
    const activeColumn = columns.find((c) => c.name().equals(aggCol));
    if (!activeColumn) {
      return null; // happens if column has actually been hidden in an above step
    }
    const activeColumnIsDate = activeColumn.type().type() === DATE;
    const changeAgg = (aggregation) => {
      const isNullable = activeColumn.type().nullable();
      return {
        section: 'changeAgg',
        aggCol,
        aggregation,
        extra:
          activeColumnIsDate || aggregation
            ? new ColumnTypeData(DATE, isNullable)
            : new ColumnTypeData(DATETIME, isNullable),
      };
    };

    // identify name to show for aggregation
    let aggName = aggCol.aggregation();
    if (!aggName) {
      if (state.expressionType.type() === DATE) {
        aggName = DAY;
      } else {
        aggName = DATE_TIME;
      }
    }

    const opts = activeColumnIsDate ? dateOptions : [DATE_TIME, ...dateOptions];

    const onClick = (aggType) => {
      if (
        (activeColumnIsDate && aggType === DAY) ||
        (!activeColumnIsDate && aggType === DATE_TIME)
      ) {
        // user doesn't actually want to aggregate, so set to null
        aggType = null;
      }
      const payload = changeAgg(aggType);
      userInteraction(payload)();
    };
    return (
      <DatetimeButtonGroup
        handleClick={onClick}
        aggName={aggName}
        options={opts}
      />
    );
  }

  buildLHS(state, columns, userInteraction, usedAliases) {
    let options = [];
    let selected = (
      <InputWithFocus
        placeholder="choose column"
        content={state.search}
        onChange={(v) => userInteraction({ section: 'search', value: v })()}
      />
    );
    if (!state.lhs) {
      // show column selector
      const extractType = (columnName) => {
        const lhsColumn = columns.find((c) => c.name().equals(columnName));
        return lhsColumn.type();
      };
      const renderer = new ColumnRenderer(
        'lhs',
        userInteraction,
        usedAliases,
        extractType,
      );
      const columnsToChoose = columns
        .filter(
          (c) =>
            c
              .name()
              .name()
              .toLowerCase()
              .indexOf(state.search.toLowerCase().trim()) !== -1,
        )
        .map((c) => c.name());
      options = (
        <ColumnsSelector values={columnsToChoose} renderer={renderer} />
      );
    } else if (this.props.forcedColumn) {
      selected = <div>{state.lhs.name()}</div>;
    } else {
      // show selected column as button
      const choice =
        state.expressionType.type() === DATE ||
        state.expressionType.type() === DATETIME
          ? this.buildDateTimeAggregationChooser(
              state,
              userInteraction,
              columns,
            )
          : null;
      selected = (
        <div className="btn-group display-flex float-left">
          <button
            type="button"
            className="btn btn-outline-dark"
            onClick={userInteraction({ section: 'lhsSelected' })}
          >
            {state.lhs.name()}
          </button>
          {choice}
        </div>
      );
    }
    return { selected, options };
  }

  buildOperator(state, columns, userInteraction) {
    let selected = (
      <input type="text" className="form-control where-operator" disabled />
    );
    let options = [];
    if (state.operator) {
      const props = { section: 'operatorSelected' };
      selected = (
        <button
          type="button"
          className="btn btn-outline-dark text-nowrap"
          onClick={userInteraction(props)}
        >
          {getOperatorSymbol(state.operator)}
        </button>
      );
    } else {
      let operators = [];
      if (
        !state.lhs ||
        !state.lhs.aggregation() ||
        state.lhs.aggregation() === DAY
      ) {
        if (state.lhs) {
          // use the operators that are valid for the selected column
          operators = state.expressionType.operators();
        } else {
          // only shows operators that apply to all possible columns
          columns.forEach(
            (c) =>
              (operators = operators.filter(
                (o) => c.type().operators().indexOf(o) !== -1,
              )),
          );
        }
      } else {
        operators = [EQ, NOT];
      }
      options = operators.map((o) => {
        const props = { section: 'operator', operator: o };
        const operatorSymbol = getOperatorSymbol(o);
        return (
          <button
            key={o}
            type="button"
            className="btn btn-outline-dark border-light-subtle"
            onClick={userInteraction(props)}
            data-test-id={`${FILTER_OPERATOR_BUTTON}:${operatorSymbol}`}
          >
            {getOperatorSymbol(operatorSymbol)}
          </button>
        );
      });
    }
    return { selected, options };
  }

  buildTimeChooser(state, userInteraction, props) {
    const timePhrase = state.rhs.split(' ');
    const timeOption = (o) => (e) =>
      userInteraction({ section: 'rhs', option: o })(e);
    const numbers = [...RANGE[timePhrase[1]]].map((i) => (
      <li key={i}>
        <a
          className="dropdown-item"
          href="#"
          onClick={timeOption(`${i} ${timePhrase.slice(1).join(' ')}`)}
        >
          {i}
        </a>
      </li>
    ));
    return (
      <div className="btn-group display-flex">
        <div className="btn-group" role="group">
          <button
            type="button"
            className="btn btn-outline-dark dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            {timePhrase[0]} <span className="caret" />
          </button>
          <ul className="dropdown-menu">{numbers}</ul>
        </div>
        <button
          type="button"
          className="btn btn-outline-dark text-nowrap"
          onClick={userInteraction(props)}
        >
          {timePhrase[1]} (s)
        </button>
        <div className="btn-group" role="group">
          <button
            type="button"
            className="btn btn-outline-dark dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            {timePhrase.slice(2).join(' ')} <span className="caret" />
          </button>
          <ul className="dropdown-menu">
            <li onClick={timeOption(`${timePhrase.slice(0, 2).join(' ')} ago`)}>
              <a className="dropdown-item" href="#">
                ago
              </a>
            </li>
            <li
              onClick={timeOption(
                `${timePhrase.slice(0, 2).join(' ')} from now`,
              )}
            >
              <a className="dropdown-item" href="#">
                from now
              </a>
            </li>
          </ul>
        </div>
      </div>
    );
  }

  buildRHS(state, columns, userInteraction) {
    function onRhsChange(e) {
      const props = { section: 'rhsValueChanged', newValue: e.target.value };
      userInteraction(props)();
    }

    let selected = (
      <input
        type="text"
        className="form-control"
        value=""
        disabled
        data-testid={FILTER_VALUE_INPUT}
      />
    );
    const rhsOptions = [];
    if (state.rhs) {
      // some value has been chosen
      const props = { section: 'rhsSelected' };
      if (state.rhs.indexOf('ago') !== -1 || state.rhs.indexOf('now') !== -1) {
        // time ago / from now, has been chosen
        selected = this.buildTimeChooser(state, userInteraction, props);
      } else {
        // fixed value has been chosen (e.g. NULL or EMPTY)
        selected = (
          <button
            type="button"
            className="btn btn-outline-dark"
            onClick={userInteraction(props)}
          >
            {state.rhs}
          </button>
        );
      }
    } else if (state.rhsColumn) {
      // column has been chosen
      const props = { section: 'rhsSelected' };
      selected = (
        <button
          type="button"
          className="btn btn-outline-dark"
          onClick={userInteraction(props)}
        >
          {state.rhsColumn.name()}
        </button>
      );
    } else if (state.lhs && state.operator) {
      // no value has yet been chosen on rhs, so show options
      if (state.operator === ONE_OF || state.operator === NOT_ONE_OF) {
        selected = (
          <textarea
            data-gramm="false"
            className="form-control"
            placeholder="list of values (one per line)"
            rows="4"
            cols="30"
            onChange={onRhsChange}
            value={state.rhsText ?? ''}
          />
        );
      } else if (state.expressionType.type() === BOOL) {
        // show true/false as options
        rhsOptions.push('TRUE');
        rhsOptions.push('FALSE');
      } else if (
        state.expressionType.type() === DATETIME ||
        state.expressionType.type() === DATE
      ) {
        let aggregation = state.lhs.aggregation();
        if (state.expressionType.type() === DATE && !state.lhs.aggregation()) {
          aggregation = DAY; // null means 'day' for DATE type;
        }
        // user wants to choose a timestamp
        if (
          [BEFORE, AFTER, EQ, NOT, LT, GT, LTE, GTE].includes(state.operator)
        ) {
          selected = (
            <DateTimePicker
              aggregation={aggregation}
              onChange={onRhsChange}
              value={state.rhsText}
            />
          );
        } else if (
          [MORE_THAN, LESS_THAN].includes(state.operator) &&
          !state.rhsText &&
          [DAY, null].includes(aggregation)
        ) {
          if (state.expressionType.type() === DATETIME) {
            rhsOptions.push('x hours ago');
          }
          rhsOptions.push('x days ago');
          rhsOptions.push('x weeks ago');
          rhsOptions.push('x months ago');
        }
        if (!state.rhsText && [EQ, NOT].includes(state.operator)) {
          switch (aggregation) {
            case DAY:
              rhsOptions.push(TODAY, YESTERDAY);
              break;
            case WEEK:
              rhsOptions.push(THIS_WEEK, LAST_WEEK);
              break;
            case MONTH:
              rhsOptions.push(THIS_MONTH, LAST_MONTH);
              break;
            case QUARTER:
              rhsOptions.push(THIS_QUARTER, LAST_QUARTER);
              break;
            case YEAR:
              rhsOptions.push(THIS_YEAR, LAST_YEAR);
              break;
          }
        }
      } else {
        // basic text input (for strings and integers)
        selected = (
          <input
            type="text"
            className="form-control"
            onChange={onRhsChange}
            value={state.rhsText ?? ''}
            placeholder="enter a value"
            data-testid={FILTER_VALUE_INPUT}
          />
        );
      }
      if (state.expressionType.nullable() && !state.rhsText) {
        // nullable types can be compared to NULL
        if (state.operator === EQ || state.operator === NOT) {
          rhsOptions.push('NULL');
        }
      }
      if (
        state.expressionType.type() === STRING &&
        !state.rhsText &&
        (state.operator === EQ || state.operator === NOT)
      ) {
        // string types can be compared to EMPTY
        rhsOptions.push('EMPTY');
      }
    }
    const options = rhsOptions.map((o) => {
      const props = { section: 'rhs', option: o };
      return (
        <button
          key={o}
          type="button"
          className="btn btn-outline-dark"
          onClick={userInteraction(props)}
        >
          {o}
        </button>
      );
    });
    return { selected, options };
  }

  buildRhsColumnOptions(state, columns, userInteraction, usedAliases) {
    if (
      state.rhsColumn ||
      !state.lhs ||
      [EQ, NOT, LT, LTE, GT, GTE, BEFORE, AFTER].indexOf(state.operator) ===
        -1 ||
      state.rhs ||
      state.rhsText
    ) {
      return [];
    }
    let underlyingIsDateTime = false;
    if (state.expressionType.type() === DATE) {
      const lhsColumn = columns.find((c) => c.name().equals(state.lhs));
      underlyingIsDateTime = lhsColumn.type().type() === DATETIME;
    }

    const viableColumns = columns.filter(
      (c) =>
        (c.type().type() === state.expressionType.type() ||
          (c.type().type() === DATETIME && underlyingIsDateTime)) && // also match columns if they can be converted
        !c.name().equals(state.lhs),
    );

    const extra = (columnName) => {
      const rhsColumn = columns.find((c) => c.name().equals(columnName));
      return {
        shouldSwitchToDateTime:
          underlyingIsDateTime && rhsColumn.type().type() === DATETIME,
      };
    };

    const renderer = new ColumnRenderer(
      'rhsColumn',
      userInteraction,
      usedAliases,
      extra,
    );

    const showColumns = () => {
      const props = { section: 'showColumns' };
      userInteraction(props)();
    };

    return state.showColumnOptions ? (
      <ColumnsSelector
        values={viableColumns.map((c) => c.name())}
        renderer={renderer}
      />
    ) : viableColumns.length > 0 ? (
      <div className="text-align-center margin-top-10">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            showColumns();
          }}
        >
          Show other columns
        </a>
      </div>
    ) : null;
  }

  renderClause(
    state,
    columns,
    userInteraction,
    usedAliases,
    index,
    isAnd,
    numberOfClauses,
    firstLineLabel,
  ) {
    columns = columns.filter((c) => c.type().operators()); // we can't build WHERE clause on types that we don't support
    if (columns.length === 0) {
      return (
        <div className="alert alert-warning" role="alert">
          Unfortunately none of the visible columns support Filter.
        </div>
      );
    }
    const lhs = this.buildLHS(state, columns, userInteraction, usedAliases);
    const operator = this.buildOperator(state, columns, userInteraction);
    const rhs = this.buildRHS(state, columns, userInteraction);
    const columnOptions = this.buildRhsColumnOptions(
      state,
      columns,
      userInteraction,
      usedAliases,
    );
    const errorMsg = !state.errorMsg ? null : (
      <div className="alert alert-danger" role="alert">
        {state.errorMsg}
      </div>
    );

    const withAutocomplete =
      state.readyForSave ||
      ((lhs.options === null || lhs.options.length === 0) &&
        (operator.options === null || operator.options.length === 0) &&
        (columnOptions === null || columnOptions.length === 0) &&
        (rhs.options === null || rhs.options.length === 0));

    const autocomplete = withAutocomplete ? null : (
      <tr>
        <td />
        <td>{lhs.options}</td>
        <td>
          <div className="btn-group-vertical modal_options">
            {operator.options}
          </div>
        </td>
        <td>
          <div className="btn-group-vertical modal_options">{rhs.options}</div>
          <div className="btn-group-vertical modal_options">
            {columnOptions}
          </div>
        </td>
      </tr>
    );

    return (
      <table key={index}>
        <thead>
          <tr>
            <td />
            <td colSpan={3}>{errorMsg}</td>
          </tr>
        </thead>
        <tbody>
          <TableRowTopAlign>
            <td>
              <span className="modal_label">
                <b>{index === 0 ? firstLineLabel : isAnd ? 'and' : 'or'}</b>
              </span>
            </td>
            <td>{lhs.selected}</td>
            <td>{operator.selected}</td>
            <td>{rhs.selected}</td>
            <td>
              {index === 0 && numberOfClauses === 1 ? null : (
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={userInteraction({ section: 'delete' })}
                >
                  <span className="glyphicon glyphicon-remove" />
                </button>
              )}
            </td>
          </TableRowTopAlign>
          {autocomplete}
        </tbody>
      </table>
    );
  }

  render() {
    const {
      state,
      columns,
      userInteraction,
      usedAliases,
      firstLineLabel = 'by',
    } = this.props;

    const clauses = state.clauses.map((clauseState, index, allClauses) => {
      const clauseInteraction = (props) => (e) => {
        props.clauseIndex = index;
        userInteraction(props)(e);
      };
      return this.renderClause(
        clauseState,
        columns,
        clauseInteraction,
        usedAliases,
        index,
        state.isAnd,
        allClauses.length,
        firstLineLabel,
      );
    });

    const addClause = (
      <button
        type="button"
        className="btn btn-outline-dark btn-md btn-add-option"
        onClick={userInteraction({ section: 'or' })}
      >
        Add another filter
      </button>
    );

    function andOrChange(e) {
      const props = { section: 'toggleAnd', newValue: e.currentTarget.value };
      userInteraction(props)();
    }

    const andOr = (
      <select
        value={state.isAnd}
        className="form-control andOr"
        onChange={andOrChange}
      >
        <option value={false}>OR</option>
        <option value>AND</option>
      </select>
    );

    return (
      <div>
        {clauses}
        {clauses.length === 1 && !state.readyForSave ? null : (
          <div>
            <hr />
            <div className="modalAddOption">
              {andOr}
              {addClause}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default ModalWhere;
